import type { DiscoveredTool } from "../../api/customMcpApi";

/** Read vs write: drives the badge and the Read / Write filter. */
export type ToolKind = "read" | "write";

export interface ToolMeta {
  category: string;
  kind: ToolKind;
  /** What the row shows in bold: the server's title, else the tool name. */
  label: string;
}

/** Section for tools a server left ungrouped while grouping others. */
export const OTHER = "Other";

// MCP has no standard grouping field, so sections come from whatever the server
// sends — never from rules about a particular connector's tool names:
//   1. the tool's category (annotations / _meta.category, read by the probe),
//   2. else its first tag (annotations.tags, _meta.tags or FastMCP's
//      _meta._fastmcp.tags),
//   3. else, when the server groups nothing at all, one section per connector
//      (what Claude Desktop / Cursor do); when it groups some tools, "Other".
const READ_VERBS = /^(get|list|lookup|describe|resolve|search|read|fetch|find|query|count|view|show)_|^whoami$/;

/** Read vs write when the server sends no readOnlyHint, from the leading verb.
 *  Unknown verbs count as write: an unrecognised tool is treated as able to
 *  change data, which is the safer thing to badge. */
function kindFromName(name: string): ToolKind {
  return READ_VERBS.test(name) ? "read" : "write";
}

function serverGroup(t: DiscoveredTool): string | undefined {
  return t.category?.trim() || t.tags?.find((tag) => tag.trim())?.trim() || undefined;
}

/** Whether the server grouped any of this connector's tools. Decided over the
 *  whole tool list (not the filtered view) so sections don't change while the
 *  user searches. */
export function serverGroupsTools(tools: DiscoveredTool[]): boolean {
  return tools.some((t) => serverGroup(t) !== undefined);
}

/** Section names compare case-insensitively ("Billing" and "billing" are one
 *  section). Maps each lower-cased name to the casing shown: that of the
 *  alphabetically-first tool in the section. Built over the whole tool list so
 *  a filtered view keeps the same label. */
export type SectionLabels = Map<string, string>;

export function sectionLabels(tools: DiscoveredTool[]): SectionLabels {
  const labels: SectionLabels = new Map();
  for (const t of [...tools].sort((a, b) => a.upstream_name.localeCompare(b.upstream_name))) {
    const group = serverGroup(t);
    if (group && !labels.has(group.toLowerCase())) labels.set(group.toLowerCase(), group);
  }
  return labels;
}

/** `ungrouped` is the section for a tool the server didn't group: the
 *  connector's name when the server groups nothing, otherwise OTHER. */
export function categorize(t: DiscoveredTool, ungrouped: string = OTHER, labels?: SectionLabels): ToolMeta {
  const kind: ToolKind =
    typeof t.read_only === "boolean" ? (t.read_only ? "read" : "write") : kindFromName(t.upstream_name);
  const group = serverGroup(t);
  const category = group ? (labels?.get(group.toLowerCase()) ?? group) : ungrouped;
  return { category, kind, label: t.title?.trim() || t.upstream_name };
}

/** Prefix that narrows a search to exact tag matches (what a tag chip sets). */
export const TAG_QUERY_PREFIX = "tag:";

/** Plain text matches the tool's name (with or without underscores), title,
 *  description and tags. `tag:<name>` matches only tools carrying that exact
 *  tag, so clicking a chip lists its tools without description noise. */
export function matchesQuery(t: DiscoveredTool, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const tags = (t.tags ?? []).map((tag) => tag.toLowerCase());
  if (q.startsWith(TAG_QUERY_PREFIX)) {
    const tag = q.slice(TAG_QUERY_PREFIX.length).trim();
    return !tag || tags.includes(tag);
  }
  const haystack = [t.upstream_name, t.upstream_name.replace(/_/g, " "), t.title ?? "", t.description, ...tags]
    .join("\n")
    .toLowerCase();
  return haystack.includes(q);
}

export function groupTools(
  tools: DiscoveredTool[],
  ungrouped: string = OTHER,
  labels: SectionLabels = sectionLabels(tools),
): { category: string; tools: DiscoveredTool[] }[] {
  const byCategory = new Map<string, DiscoveredTool[]>();
  for (const t of tools) {
    const { category } = categorize(t, ungrouped, labels);
    const list = byCategory.get(category) ?? [];
    list.push(t);
    byCategory.set(category, list);
  }
  // Alphabetical, with the catch-all section last.
  const last = (c: string) => (c === OTHER ? 1 : 0);
  return [...byCategory.entries()]
    .sort(([a], [b]) => last(a) - last(b) || a.localeCompare(b))
    .map(([category, list]) => ({
      category,
      tools: [...list].sort((a, b) => a.upstream_name.localeCompare(b.upstream_name)),
    }));
}
